/*
 * PROD-7828 R5 — pure request builders for the Overview endpoints. Every enum here mirrors a goserver
 * whitelist (overview_plan.go); params.test.js pins them against the backend-generated overview_enums.json.
 */

// row_state: overview_plan.go l1RowStateFilters. The UI only exposes the needs-review filter.
export const ROW_STATE_NEEDS_REVIEW = 'needs_review';

// kind: overview_plan.go drilldownKind*.
export const DRILLDOWN_KIND = { ITEMS: 'items', PENALTIES: 'penalties', ALL: 'all' };

// The ADDITIONAL category answers with additional-payment entries: response and rows carry this kind.
export const DRILLDOWN_CATEGORY_ADDITIONAL = 'ADDITIONAL';
export const DRILLDOWN_KIND_ADDITIONAL = 'additional';

// overview_plan.go catUnattributed: penalties not linked to a session type (manual, month-level, legacy NULL category).
export const DRILLDOWN_CATEGORY_UNATTRIBUTED = 'UNATTRIBUTED';

// overview_plan.go sessionStatusUnknown: session rows whose status was never recorded (legacy NULL).
export const SESSION_STATUS_UNKNOWN = 'UNKNOWN';

// Mirrors validateDrilldownParams: an upper-snake status of at most 48 chars; anything else is not sent.
const SESSION_STATUS_RE = /^[A-Z_]{1,48}$/;

// teacher_status: overview_plan.go teacherStatusValues ('' = all, never sent).
export const TEACHER_STATUS_OPTIONS = [
  { value: 'APPROVED', label: 'Approved' },
  { value: 'TEMPORARILY_UNAVAILABLE', label: 'Temporarily unavailable' },
  { value: 'UNDER_PROBATION', label: 'Under probation' },
  { value: 'VISHESH_TEACHER', label: 'Vishesh teacher' },
  { value: 'RESIGNED', label: 'Resigned' },
  { value: 'SUSPENDED', label: 'Suspended' }
];

const TEACHER_STATUS_VALUES = TEACHER_STATUS_OPTIONS.map((o) => o.value);

// sort: overview_plan.go l1SortColumns (whitelisted server-side); dir: overview_plan.go parseSortDir.
export const L1_SORT_KEYS = [
  'teacher_name',
  'teacher_slug',
  'net',
  'gross',
  'one_on_one',
  'group_class',
  'trial',
  'incentives',
  'additional',
  'penalties',
  'waivers',
  'free_pass_balance',
  'lifecycle',
  'base_rate_snapshot',
  'rounded_net_payable'
];
export const SORT_DIRS = ['asc', 'desc'];
export const DEFAULT_L1_SORT = 'teacher_name';
export const DEFAULT_L1_DIR = 'asc';

// Drops empty/nullish values so the backend applies its "all" default.
export const build_query_string = (params = {}) => {
  const qs = new URLSearchParams();
  Object.keys(params).forEach((k) => {
    const v = params[k];
    if (v === null || v === undefined || v === '') return;
    qs.append(k, String(v));
  });
  return qs.toString();
};

// Shared L1/CSV filter set; unknown UI values are dropped rather than sent (the backend would 400).
const overview_filters = ({ pay_month, search, needs_review_only, teacher_status }) => ({
  pay_month,
  search: (search || '').trim(),
  row_state: needs_review_only ? ROW_STATE_NEEDS_REVIEW : '',
  teacher_status: TEACHER_STATUS_VALUES.indexOf(teacher_status) === -1 ? '' : teacher_status
});

const sort_filters = (sort) => ({
  sort: L1_SORT_KEYS.indexOf(sort && sort.key) === -1 ? DEFAULT_L1_SORT : sort.key,
  dir: SORT_DIRS.indexOf(sort && sort.dir) === -1 ? DEFAULT_L1_DIR : sort.dir
});

export const l1_params = (filters, limit, offset) => ({
  ...overview_filters(filters),
  ...sort_filters(filters.sort),
  limit,
  offset
});

export const csv_params = (filters) => ({ ...overview_filters(filters), ...sort_filters(filters.sort) });

export const drilldown_params = ({ teacher_uuid, pay_month, category, kind, session_status }) => ({
  teacher_uuid,
  pay_month,
  category,
  kind: Object.values(DRILLDOWN_KIND).indexOf(kind) === -1 ? DRILLDOWN_KIND.ALL : kind,
  session_status: SESSION_STATUS_RE.test(session_status || '') ? session_status : ''
});

// ---- deep link (the daily Slack digest: /teacher-payments/overview?pay_month=YYYY-MM&row_state=needs_review) ----

const PAY_MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

// Pure: reads only the two whitelisted params; anything malformed is dropped so the page falls back to its defaults.
export const parse_overview_deep_link = (search) => {
  const qs = new URLSearchParams(typeof search === 'string' ? search : '');
  const pay_month = (qs.get('pay_month') || '').trim();
  const row_state = (qs.get('row_state') || '').trim().toLowerCase();
  return {
    pay_month: PAY_MONTH_RE.test(pay_month) ? pay_month : '',
    needs_review_only: row_state === ROW_STATE_NEEDS_REVIEW
  };
};

// The linked month only when it is one the backend listed; otherwise the newest month (the default).
export const pick_initial_month = (months, requested) => {
  const list = Array.isArray(months) ? months : [];
  if (requested && list.some((m) => m && m.pay_month === requested)) return requested;
  return list.length ? list[0].pay_month : '';
};

// D1: a picker value is taken only when it is one of the listed months; the empty "Select a month" option is ignored.
export const accept_month_choice = (value, months) =>
  !!value && (Array.isArray(months) ? months : []).some((m) => m && m.pay_month === value);

// ---- L1 view state (D3): lifted into OverviewTab so Back from L2 restores filters, drafts and page ----

// search (the location's query string, optional): a ?teacher_uuid= link opens straight to that teacher,
// the same filter the Teacher filter box applies — the display name is filled in once a row lands (D3's
// drafts/Apply covers the other filters, which are not link-shareable on their own).
export const initial_l1_state = (needs_review_only, search = '') => {
  const qs = new URLSearchParams(typeof search === 'string' ? search : '');
  const teacher_uuid = (qs.get('teacher_uuid') || '').trim();
  return {
    month: '',
    offset: 0,
    applied: { search: teacher_uuid, needs_review_only: !!needs_review_only, teacher_status: '' },
    drafts: {
      search: teacher_uuid,
      search_display: '',
      review: needs_review_only ? 'NEEDS_REVIEW' : '',
      status: ''
    },
    sort: { key: DEFAULT_L1_SORT, dir: DEFAULT_L1_DIR }
  };
};

// A header click: like Apply, goes back to page 1; the sort itself survives Back from L2 (D3).
export const apply_l1_sort = (state, sort) => ({ ...state, offset: 0, sort });

// Same month → the state as left (page kept); a different month → page 1, filters kept. Returns the SAME object when
// nothing changes so the caller can skip the update.
export const l1_state_for_month = (state, pay_month) =>
  state.month === pay_month ? state : { ...state, month: pay_month, offset: 0 };

// Apply turns the drafts into the query filters and goes back to page 1.
export const apply_l1_drafts = (state) => ({
  ...state,
  offset: 0,
  applied: {
    search: (state.drafts.search || '').trim(),
    needs_review_only: state.drafts.review === 'NEEDS_REVIEW',
    teacher_status: state.drafts.status
  }
});
